import { BudgetRequestListItemDto } from '../../../types';
import { useBrgList } from '../../../hooks/useBrgList';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { UserDropdownDto } from '../../../types';
import { useAuth } from '../../../auth/useAuth';

interface BudgetRequestDetailsModalProps {
  request: BudgetRequestListItemDto;
  onClose: () => void;
  onEdit?: (request: BudgetRequestListItemDto) => void;
  onDelete?: (request: BudgetRequestListItemDto) => void;
  isAdmin?: boolean;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}

function formatScheduledDate(dateString?: string): string {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  const m = String(parseInt(month)).padStart(2, '0');
  const d = String(parseInt(day)).padStart(2, '0');
  return `${m}/${d}/${year}`;
}

export function BudgetRequestDetailsModal({
  request,
  onClose,
  onEdit,
  onDelete,
  isAdmin = false,
}: BudgetRequestDetailsModalProps) {
  const { email } = useAuth();
  const { data: brgs = [] } = useBrgList();
  // Fetch all users (including inactive) to ensure we can resolve all requesters
  const { data: users = [] } = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => apiClient.get<UserDropdownDto[]>('/api/users?active=false'),
  });
  
  // Find BRG name from dynamic data
  const brgName = brgs.find(b => b.brgId === request.brgId)?.name || `BRG ${request.brgId}`;
  
  // Find user name from dynamic data
  const submittedBy = users.find(u => u.userId === request.requestedById)?.displayName || '—';
  const canDelete = request.budgetStatusId === 1 || request.budgetStatusId === 3;
  const currentUserId = users.find(
    (u) => !!email && u.email.toLowerCase() === email.toLowerCase()
  )?.userId;
  const isOwnRequest = !!currentUserId && request.requestedById === currentUserId;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(20, 30, 50, 0.55)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          width: '560px',
          maxWidth: '95vw',
          maxHeight: '88vh',
          overflowY: 'auto',
          boxShadow: '0 8px 40px rgba(0, 0, 0, 0.25)',
        }}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: '#1a3bcc',
            color: 'white',
            padding: '16px 22px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2 style={{ fontSize: '15px', fontWeight: 800, margin: 0 }}>Budget Request Details</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '0 4px',
              fontWeight: 700,
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '18px 22px', maxHeight: '60vh', overflowY: 'auto' }}>
          {/* Status Badge */}
          {request.budgetStatusId !== 1 && (
            <div style={{ marginBottom: '16px', textAlign: 'center' }}>
              <span
                style={{
                  display: 'inline-block',
                  fontSize: '13px',
                  fontWeight: 700,
                  padding: '8px 16px',
                  borderRadius: '4px',
                  backgroundColor: request.budgetStatusId === 2 ? '#d4edda' : '#f8d7da',
                  color: request.budgetStatusId === 2 ? '#1a7c3a' : '#a01a23',
                }}
              >
                {request.budgetStatusId === 2 ? '✓ This request has been Approved' : '✗ This request has been Denied'}
              </span>
            </div>
          )}

          {/* BRG Information Section */}
          <div style={{ marginBottom: '16px' }}>
            <div
              style={{
                fontSize: '10px',
                fontWeight: 800,
                color: '#888',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                borderBottom: '1px solid #eee',
                paddingBottom: '5px',
                marginBottom: '12px',
              }}
            >
              BRG Information
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#444',
                  marginBottom: '4px',
                  display: 'block',
                }}
              >
                BRG Name
              </label>
              <div
                style={{
                  background: '#f8f9fb',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  color: '#222',
                }}
              >
                {brgName}
              </div>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#444',
                  marginBottom: '4px',
                  display: 'block',
                }}
              >
                Submitted By
              </label>
              <div
                style={{
                  background: '#f8f9fb',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  color: '#222',
                }}
              >
                {submittedBy}
              </div>
            </div>
          </div>

          {/* Request Details Section */}
          <div>
            <div
              style={{
                fontSize: '10px',
                fontWeight: 800,
                color: '#888',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                borderBottom: '1px solid #eee',
                paddingBottom: '5px',
                marginBottom: '12px',
              }}
            >
              Request Details
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#444',
                  marginBottom: '4px',
                  display: 'block',
                }}
              >
                Request Amount
              </label>
              <div
                style={{
                  background: '#f8f9fb',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  color: '#222',
                }}
              >
                {formatCurrency(request.requestAmount)}
              </div>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#444',
                  marginBottom: '4px',
                  display: 'block',
                }}
              >
                Project / Event
              </label>
              <div
                style={{
                  background: '#f8f9fb',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  color: '#222',
                }}
              >
                {request.projectEvent || '—'}
              </div>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#444',
                  marginBottom: '4px',
                  display: 'block',
                }}
              >
                Scheduled Date
              </label>
              <div
                style={{
                  background: '#f8f9fb',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  color: '#222',
                }}
              >
                {request.scheduledDate ? formatScheduledDate(request.scheduledDate) : '—'}
              </div>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#444',
                  marginBottom: '4px',
                  display: 'block',
                }}
              >
                Purpose / Description
              </label>
              <div
                style={{
                  background: '#f8f9fb',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  color: '#222',
                  minHeight: '32px',
                }}
              >
                {request.purposeDescription || '—'}
              </div>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#444',
                  marginBottom: '4px',
                  display: 'block',
                }}
              >
                Request Description
              </label>
              <div
                style={{
                  background: '#f8f9fb',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  color: '#222',
                  minHeight: '60px',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                }}
              >
                {request.requestDescription || '—'}
              </div>
            </div>

            {/* Requests auto-created from Project Tracking have no Impact */}
            {!request.projectId && (
              <div style={{ marginBottom: '12px' }}>
                <label
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#444',
                    marginBottom: '4px',
                    display: 'block',
                  }}
                >
                  Impact
                </label>
                <div
                  style={{
                    background: '#f8f9fb',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    fontSize: '12px',
                    color: '#222',
                    minHeight: '60px',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                  }}
                >
                  {request.impact || '—'}
                </div>
              </div>
            )}

            {request.budgetStatusId === 3 && (
              <div style={{ marginBottom: '12px' }}>
                <label
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#444',
                    marginBottom: '4px',
                    display: 'block',
                  }}
                >
                  Reason for Denial
                </label>
                <div
                  data-testid="deny-reason-display"
                  style={{
                    background: '#fdf3f4',
                    border: '1px solid #f1c2c6',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    fontSize: '12px',
                    color: '#222',
                    minHeight: '32px',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                  }}
                >
                  {request.denyReason || '—'}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            padding: '14px 22px 20px',
            borderTop: '1px solid #eee',
          }}
        >
          {!isAdmin && canDelete && isOwnRequest && onDelete && (
            <button
              onClick={() => onDelete(request)}
              style={{
                background: '#fff',
                color: '#a01a23',
                border: '1px solid #a01a23',
                borderRadius: '4px',
                padding: '9px 24px',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Delete
            </button>
          )}
          {isAdmin && onEdit && (
            <button
              onClick={() => onEdit(request)}
              style={{
                background: '#1a3bcc',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                padding: '9px 24px',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Edit
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
